import { useContext, useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';
import { ImperativeContext } from './imperative-context';

/**
 * The bridge component. Mount once inside <ReactFlowProvider> (i.e.
 * inside CanvasEngine's body) — calls useReactFlow() and writes the
 * imperative API to the provider's ref. Renders nothing.
 *
 * Why a separate component instead of a hook in the engine: the
 * imperative API needs to flow through React context to portal-
 * sibling panels. The bridge component is the seam — it's the one
 * place that lives inside the RF tree AND has access to the
 * provider's ref via context.
 *
 * No-op when there's no <ImperativeProvider> ancestor (the engine is
 * mounted standalone, e.g. CanvasViewer). The bridge silently does
 * nothing rather than throwing — a dropped feature is preferable to
 * a runtime error.
 */
export function Imperative() {
  const ref = useContext(ImperativeContext);
  const rf = useReactFlow();

  useEffect(() => {
    if (!ref) return;
    // Snapshot the methods at mount. The RF instance returned by
    // useReactFlow is stable across renders for a given provider, so
    // capturing once is fine.
    ref.current = {
      setCenter: (x, y, opts) => rf.setCenter(x, y, opts),
      setViewport: (vp, opts) => rf.setViewport(vp, opts),
      fitView: (opts) => rf.fitView(opts),
      getViewport: () => rf.getViewport(),
      setNodes: (updater) => rf.setNodes(updater),
      getNodes: () => rf.getNodes(),
    };
    return () => {
      // Clear on unmount so a stale handle doesn't outlive the engine
      // (e.g. a canvas swap). Consumers that called useImperative()
      // and held the result will see future calls no-op against the
      // null check inside the hook.
      ref.current = null;
    };
  }, [ref, rf]);

  return null;
}
